import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { X, Link2, Users, Share2, Check } from 'lucide-react';
import { getActiveReferral, copyReferral, getReferralStats } from '../actions/referralActions';

const ReferralModal = ({
  isOpen,
  onClose,
  referral,
  getActiveReferral,
  copyReferral,
  getReferralStats
}) => {
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getActiveReferral();
      getReferralStats();
    }
  }, [isOpen]);

  const handleCopyLink = async () => {
    if (!referral.activeCode || !referral.programId) return;
    let url = `${window.location.origin}/register?ref=${referral.activeCode}&programId=${referral.programId}`;
    if (referral.pathwayId) {
      url += `&pathwayId=${referral.pathwayId}`;
    }
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
      // Mark as shared (code stays the same for reuse)
      copyReferral();
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen) return null;

  const { stats } = referral;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[60]"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1086a4] to-[#054581] px-6 py-5 text-white relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-xl p-2.5">
                <Share2 className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Refer a Friend</h2>
                <p className="text-white/70 text-sm mt-0.5">Share your code and grow the community</p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5 space-y-5">
            {/* Copy Link Button */}
            <button
              onClick={handleCopyLink}
              disabled={referral.loading || !referral.activeCode}
              className="w-full flex items-center justify-center gap-2 bg-[#1086a4] hover:bg-[#0d7490] text-white rounded-xl px-4 py-3 text-sm font-semibold transition-all disabled:opacity-50"
            >
              {linkCopied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
              {linkCopied ? 'Copied!' : 'Copy Referral Link'}
            </button>

            {/* Stats */}
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#1086a4]">{stats.used}</div>
                <div className="text-xs text-gray-500 mt-1">Friends Joined</div>
              </div>
            </div>

            {/* Referred Users List */}
            {stats.referredUsers && stats.referredUsers.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Friends who joined
                </h3>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {stats.referredUsers.map((ref) => (
                    <div key={ref._id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-700 font-medium">
                        {ref.usedBy?.name} {ref.usedBy?.lastName}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(ref.usedAt).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className="text-xs text-gray-400 text-center">
              Your link is reusable — share it with as many friends as you like!
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = state => ({
  referral: state.referral
});

export default connect(mapStateToProps, {
  getActiveReferral,
  copyReferral,
  getReferralStats
})(ReferralModal);
